import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationsAPI } from '../services/api';
import { toast } from '../components/common/Toast';
import { Bell, CheckCheck, CreditCard, Wrench, FileText, Package } from 'lucide-react';
import { timeAgo } from '../utils/helpers';

const TYPE_CFG = {
  payment_overdue:      { icon: CreditCard, color:'var(--rose)',    bg:'rgba(225,29,72,0.1)' },
  payment_received:     { icon: CreditCard, color:'var(--emerald)', bg:'rgba(5,150,105,0.1)' },
  work_order:           { icon: Wrench,     color:'var(--amber)',   bg:'rgba(245,158,11,0.1)' },
  work_order_completed: { icon: Wrench,     color:'var(--emerald)', bg:'rgba(5,150,105,0.1)' },
  lease_expiring:       { icon: FileText,   color:'var(--amber)',   bg:'rgba(245,158,11,0.1)' },
  inventory_low:        { icon: Package,    color:'var(--violet)',  bg:'rgba(124,58,237,0.1)' },
  maintenance_scheduled:{ icon: Wrench,     color:'var(--brand)',   bg:'rgba(37,99,235,0.1)' },
  general:              { icon: Bell,       color:'var(--ink-40)',  bg:'var(--surface-3)' },
};
const SEV_BADGE = { error:'badge-overdue', warning:'badge-pending', success:'badge-active', info:'badge-info' };

export default function Notifications() {
  const { t }              = useTranslation();
  const [notifs, setNotifs]   = useState([]);
  const [unread, setUnread]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    notificationsAPI.getAll()
      .then(r => { setNotifs(r.data.data); setUnread(r.data.unreadCount); })
      .catch(()=>toast.error(t('failedToLoad')))
      .finally(()=>setLoading(false));
  }, [t]);

  useEffect(()=>{ load(); },[load]);

  const filtered = notifs.filter(n => {
    if (filter==='unread')  return !n.read;
    if (filter==='error')   return n.severity==='error';
    if (filter==='warning') return n.severity==='warning';
    return true;
  });

  const markRead = async id => {
    try { await notificationsAPI.markRead(id); setNotifs(ns=>ns.map(n=>n._id===id?{...n,read:true}:n)); setUnread(u=>Math.max(0,u-1)); }
    catch{}
  };

  const markAll = async () => {
    try { await notificationsAPI.markAllRead(); setNotifs(ns=>ns.map(n=>({...n,read:true}))); setUnread(0); toast.success(t('allMarkedAsRead')); }
    catch(err){ toast.error(t('failedToLoad')); }
  };

  const del = async id => {
    try { await notificationsAPI.delete(id); setNotifs(ns=>ns.filter(n=>n._id!==id)); }
    catch{}
  };

  return (
    <div>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">{t('notifications')}</h1>
          <p className="page-subtitle">{unread} {t(unread !== 1 ? 'unreadNotifications' : 'unreadNotification')}</p>
        </div>
        {unread>0&&<button className="btn btn-secondary" onClick={markAll}><CheckCheck size={16}/>{t('markAllRead')}</button>}
      </div>

      <div className="tabs mb-6" style={{ width:'fit-content' }}>
        {[
          { k:'all',     l: t('tabAll') },
          { k:'unread',  l: `${t('tabUnread')} (${unread})` },
          { k:'error',   l: t('tabUrgent') },
          { k:'warning', l: t('tabWarnings') },
        ].map(({k,l})=>(
          <div key={k} className={`tab${filter===k?' active':''}`} onClick={()=>setFilter(k)}>{l}</div>
        ))}
      </div>

      {loading ? <div style={{ display:'flex',justifyContent:'center',padding:60 }}><div className="spinner"/></div> : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {filtered.length===0&&(
            <div className="empty-state"><Bell size={40}/><h3>{t('noNotifications')}</h3><p style={{ fontSize:14 }}>{t('allCaughtUp')}</p></div>
          )}
          {filtered.map(n=>{
            const cfg  = TYPE_CFG[n.type] || TYPE_CFG.general;
            const Icon = cfg.icon;
            return (
              <div key={n._id} className="card" style={{ padding:'16px 20px', display:'flex', gap:14, alignItems:'flex-start', cursor:'pointer', border:!n.read?'1px solid var(--brand)':'1px solid var(--border)', background:!n.read?'rgba(37,99,235,0.02)':'var(--white)', transition:'box-shadow 130ms' }}
                onMouseEnter={e=>e.currentTarget.style.boxShadow='var(--shadow-md)'}
                onMouseLeave={e=>e.currentTarget.style.boxShadow=''}
                onClick={()=>!n.read&&markRead(n._id)}>
                <div style={{ width:40,height:40,borderRadius:11,background:cfg.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                  <Icon size={19} color={cfg.color}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:14 }}>{n.title}</span>
                    <span className={`badge ${SEV_BADGE[n.severity]||'badge-neutral'}`}>{n.severity}</span>
                    {!n.read&&<span style={{ width:7,height:7,borderRadius:'50%',background:'var(--brand)',flexShrink:0,marginLeft:'auto'}}/>}
                  </div>
                  <p style={{ fontSize:13,color:'var(--ink-60)',lineHeight:1.5,marginBottom:5 }}>{n.message}</p>
                  <span style={{ fontSize:11,color:'var(--ink-20)' }}>{timeAgo(n.createdAt)}</span>
                </div>
                <button className="btn btn-ghost btn-icon" style={{ color:'var(--ink-20)',flexShrink:0 }} onClick={e=>{e.stopPropagation();del(n._id);}}>×</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
